// ---------------------------------------------------------------------------
// Bioregional Ventures — editable site content.
//
// This file holds the copy most likely to change as the company develops.
// Edit the text below and the site updates on rebuild. Page structure lives
// in src/pages/. Nothing here should claim completed projects, partnerships
// or results that have not been agreed for publication.
// ---------------------------------------------------------------------------

// Set to false to hide the "Current enquiries" section on the Our Work page.
export const SHOW_CURRENT_ENQUIRIES = true;

export const brand = {
  name: "Bioregional Ventures",
  tagline: "Land. Livelihoods. Lasting community benefit.",
  location: "",
};

export const hero = {
  eyebrow: "The science of regeneration. The Art of Possibility.",
  headline: "Building community assets. Growing local livelihoods.",
  body: [
    "Bioregional Ventures helps communities, landholders and organisations develop practical projects that connect care for land with local livelihoods.",
    "We bring people, knowledge and resources together to explore opportunities, test what can work and establish the foundations for lasting enterprises and shared assets.",
  ],
};

export const workAreas = [
  {
    number: "01",
    title: "Land and shared assets",
    text: "Explore how land, facilities and equipment can support productive use, community access and long-term stewardship.",
  },
  {
    number: "02",
    title: "Local enterprises and livelihoods",
    text: "Develop enterprise concepts around real needs, capable operators and viable markets, with attention to the relationships and infrastructure that help them succeed.",
  },
  {
    number: "03",
    title: "Food and ecological resilience",
    text: "Connect food growing, seed systems and ecological restoration with practical opportunities for local participation and employment.",
  },
  {
    number: "04",
    title: "Project development and partnerships",
    text: "Bring the right people together, assess options and develop staged plans for delivery, funding and ongoing operation.",
  },
];

export const closingStatement =
  "Start with a place, a practical need and people willing to work together.";

// --- Homepage: A Bioregional Approach ---------------------------------------
// Includes the owner's Traditional Owners sentence (formerly the standalone
// bioregional explainer, which this section replaces to avoid duplication).
export const bioregionalApproach = {
  heading: "A Bioregional Approach",
  body: [
    "Land, water, food, livelihoods and community wellbeing are interconnected. A bioregional approach starts with the ecological character, cultural relationships and practical possibilities of each place.",
    "We work with local knowledge and relevant expertise to develop projects that strengthen communities and the landscapes that sustain them. For work on Country, this includes respecting Traditional Owners' continuing authority and relationships with land.",
  ],
  definition:
    "A bioregion is a place understood through its climate, landforms, waterways, plants and animals, and the relationships people develop with them. Its boundaries emerge through ecology and lived experience, rather than administrative lines.",
  image: {
    src: "/our-work-farm.jpg",
    alt: "Aerial view of a working farm: crop rows with irrigation channels, solar panel arrays, ponds and a farm shed.",
    caption: "Photo: community farm",
  },
};

// --- Homepage services overview ----------------------------------------------

export const servicesOverview = {
  heading: "Ways to work with us",
  intro: "",
  cards: [
    {
      title: "Project Development & Consulting",
      text: "Feasibility, business planning, governance, partnerships, funding readiness and practical implementation support.",
      linkLabel: "Explore our services",
      href: "/our-work",
    },
    {
      title: "Funding & Capital Partnerships",
      text: "Funding research, grants, philanthropic engagement, financing strategy and exploration of appropriate relationships with private investors and other aligned capital partners.",
      linkLabel: "Explore funding",
      href: "/funding",
      linkLabel2: "Beyond Grants",
      href2: "/funding/beyond-grants",
    },
    {
      title: "Bioregional Capital Partners - Investment",
      text: "Bioregional Capital Partners is building a portfolio of regenerative investment opportunities focused on productive landscapes, resilient enterprises and long-term value creation.",
      linkLabel: "Explore Capital Partners",
      href: "/capital-partners",
    },
  ],
  ctaLabel: "Discuss your project",
  ctaSupport: "Tell us about the place, the people involved and what you want to make possible.",
};

// --- Ask Gaia (home page chatbot section) -----------------------------------

export const gaia = {
  eyebrow: "Ask Gaia",
  heading: "Meet Gaia",
  intro:
    "Explore what's possible for your land and community. Ask Gaia about community ownership, cooperative farming, ecological restoration, food security and funding pathways. Get practical guidance to shape your thinking and identify your next step.",
  embedUrl: "https://builder.formwise.ai/tools/11ce28e3-78f5-4b3d-bbe2-b0774c9e06b7",
  fallbackLabel: "Explore your project with Gaia",
  inviteHeading: "Ready to develop your project?",
  inviteBody:
    "Gaia can help you explore the possibilities. For advice tailored to your land, organisation or enterprise, Bioregional Ventures offers consulting, feasibility assessment, governance development, funding strategy and project management.",
  inviteTail:
    "If you would like to connect with one of our team, click on the button below.",
  inviteButton: "Discuss your project with our team",
  note: "Gaia provides AI-generated guidance. Project-specific legal, tax and investment decisions require appropriate professional advice.",
};

// --- Our Work page ----------------------------------------------------------

export const serviceDetails = [
  {
    title: "Community land and shared assets",
    text: "Research and development for community land trusts, cooperative arrangements and other forms of shared stewardship. We assess purpose, tenure, governance, affordability and long-term obligations.",
  },
  {
    title: "Community farms and local enterprise",
    text: "Exploring land access, compatible enterprises, shared infrastructure, production requirements and routes to market. Initial interests include native grains, culinary and medicinal herbs, seeds and appropriately assessed botanical products.",
  },
  {
    title: "Restoration and stewardship",
    text: "Identifying restoration priorities and the resources needed to deliver and maintain them. We distinguish enterprise costs from wider ecological and community benefits that may require separate support.",
  },
  {
    title: "Project coordination and management",
    text: "Supporting feasibility studies, partner engagement, specialist advice, funding proposals, implementation planning and ongoing project coordination.",
  },
];

export const currentEnquiries = {
  label: "Exploratory work — subject to feasibility, partnerships and approvals.",
  items: [
    "Cooperative farming and shared infrastructure.",
    "Community ownership and adaptive reuse of existing properties.",
    "Funding for restoration alongside small enterprise.",
  ],
};

// --- Approach page ----------------------------------------------------------

export const processSteps = [
  {
    number: "01",
    title: "Understand the place.",
    text: "Start with local priorities, existing knowledge, relationships, assets and ecological conditions. Establish who needs to be involved and what decisions are theirs to make.",
  },
  {
    number: "02",
    title: "Shape the opportunity.",
    text: "Develop a shared project concept and assess options, demand, dependencies and constraints.",
  },
  {
    number: "03",
    title: "Test the foundations.",
    text: "Examine operating costs, responsibilities, ownership options and the capacity required to carry the work forward. Use small trials where they can resolve important uncertainties.",
  },
  {
    number: "04",
    title: "Build the pathway.",
    text: "Prepare a staged budget, identify suitable funding and delivery partners, and establish clear next steps.",
  },
  {
    number: "05",
    title: "Support delivery and learning.",
    text: "Where agreed, support implementation, coordinate contributors and review progress against practical measures.",
  },
];

export const earlyQuestions = [
  "Who is this for?",
  "Can it support the people doing the work?",
  "Who pays for maintenance and shared responsibilities?",
  "What happens if income falls or a key person leaves?",
  "How is the long-term purpose protected?",
  "What evidence would tell us to proceed, revise or stop?",
];

// --- About page -------------------------------------------------------------

export const aboutHeading = "A life's work connecting people, land and possibility.";

export const founderBio = [
  "William Martin co-founded Bioregional Ventures to bring his experience in organic agriculture, community organisations, Indigenous partnerships and project leadership into practical work around land and local livelihoods.",
  "Originally from Tucson, Arizona, William's experience spans running a 28-acre organic farm, heirloom seed business and community-supported agriculture initiative in California; working with the Buckminster Fuller Institute; and nearly a decade living and working alongside Aboriginal communities in Australia's Central Desert. His work has included community gardens, seed banks, bush foods and medicines, ecological restoration and cultural knowledge initiatives.",
  "William attended Schumacher College, where he developed lasting friendships with Satish Kumar and Dr Vandana Shiva. These relationships deepened his commitment to ecological responsibility, local livelihoods and an economy that serves life.",
  "He has served as CEO of Indigenous organisations and managed land and economic development programmes. With Queensland Parks and Wildlife Service, he worked on land tenure agreements, economic development projects and native title matters, supporting relationships between Traditional Owners and government.",
  "Passionate about local food security, William also brings these interests into everyday community life through Native Grains, his weekend farmers market stall.",
  "Across these settings, his strength is connecting people, knowledge and resources to move worthwhile ideas forward. Through Bioregional Ventures, he applies that experience to community land, cooperative farming and restoration projects - with attention to sound governance, financial viability and supporting the people and communities whose work brings these place-based initiatives to life.",
];

export const workingPrinciples = [
  "Start with local needs and knowledge.",
  "Respect Indigenous authority and cultural knowledge.",
  "Make responsibilities and costs explicit.",
  "Protect time and resources through staged decisions.",
  "Build viable livelihoods alongside community benefit.",
  "Do less, better.",
];

// Related work William is involved in — independent efforts, not Bioregional
// Ventures projects.
export const relatedWorkIntro =
  "William is involved in Bush Botanics and SeedKeepers — related work that informs this practice. These are independent efforts, not Bioregional Ventures projects.";

// --- About page: grounded in people and place (content consolidation brief,
// 26 October 2026 - replaces the three earlier philosophy sections) ----------
export const groundedInPlace = {
  heading: "Grounded in people and place",
  body: [
    "Every landscape has its own ecological conditions, cultural relationships and economic possibilities. Our work starts with those realities and the people who know them. We bring practical experience and relevant expertise together to help worthwhile projects take shape and endure.",
  ],
};

export const relatedWork = [
  { name: "Bush Botanics", url: "https://www.bushbotanics.com" },
  { name: "SeedKeepers", url: "https://www.seedkeepers.com" },
];

// --- About page: finance philosophy (master strategy brief) ------------------

export const financeThatServes = {
  heading: "Finance in Service of Life",
  body: [
    "How a project is financed shapes who owns it, who makes decisions and who benefits. We explore ways for capital to support useful work, strengthen local ownership and restore the living systems that sustain us - with the financial discipline needed to make that work endure.",
  ],
};

// --- Contact page -----------------------------------------------------------

export const contactHeading = "What are you working towards?";

export const contactIntro =
  "Tell us about your place, your project and the decision or challenge in front of you. We can explore whether Bioregional Ventures is a useful partner and what an appropriate first step could look like.";

export const contactTopics = [
  { value: "project-development", label: "Project development" },
  { value: "design-studio", label: "Design Studio" },
  { value: "common-ground", label: "Common Ground" },
  { value: "partnership", label: "Partnership" },
  { value: "other", label: "Other" },
];

export const stageOptions = [
  { value: "idea", label: "Idea" },
  { value: "early-research", label: "Early research" },
  { value: "feasibility", label: "Feasibility" },
  { value: "implementation", label: "Implementation" },
];


// --- Bioregional Design Studios (home intro + Approach page section) --------
// An emerging collaborative project development offer within Bioregional
// Ventures. No physical facilities, operating network or completed projects
// are implied. Participation does not imply funding approval or investment.

export const designStudiosHome = {
  heading: "Bioregional Design Studios",
  standfirst: "Where place meets possibility.",
  attribution: "A creative practice from Bioregional Ventures.",
  body: [
    "Bioregional Design Studios bring people together to explore local strengths, test ideas and shape practical projects.",
    "Facilitated conversations, research and small trials help turn aspirations into proposals people can assess and develop.",
  ],
  ctaLabel: "Explore Bioregional Design Studios",
  ctaHref: "/approach#design-studios",
};

export const designStudios = {
  heading: "Bioregional Design Studios",
  standfirst: "Where place meets possibility.",
  intro: [
    "Bioregional Design Studios bring community members, landholders, practitioners and relevant specialists together to shape practical projects around a place.",
    "Through collaborative workshops and community engagement, we explore local priorities, connect existing strengths and identify what is worth developing. Each studio is shaped by the ecological and cultural character of the place and what participants can realistically sustain.",
    "The work leads towards clear choices: what to pursue, what to test, who needs to be involved and what happens next.",
  ],
  exploreHeading: "What we explore",
  explore: [
    { title: "Place and relationships", text: "Bring together local experience and available evidence about land, water, food systems, livelihoods and community priorities, recognising the knowledge and authority held locally." },
    { title: "Connections and trade-offs", text: "Explore how different activities can support one another, and examine ecological limits, resource needs, maintenance obligations and long-term consequences." },
    { title: "Practical testing and local capability", text: "Use field visits, feasibility work and small trials to test assumptions before committing resources, building the skills and decision-making practices that remain with the people involved." },
  ],
  outputsHeading: "What we develop together",
  outputsIntro: "Each engagement responds to the place, the people involved and the decisions ahead. Together, we may develop:",
  outputs: [
    "A map of local assets, skills, relationships and priorities.",
    "A shared project concept and options assessment.",
    "A practical trial or demonstration plan.",
    "An enterprise and operating model.",
    "Ownership and governance options for specialist assessment.",
    "A staged budget, funding pathway and implementation plan.",
    "A proportionate framework for monitoring and learning.",
  ],
  trustHeading: "Knowledge, authority and trust",
  trustBody: [
    "Local knowledge is held within relationships and responsibilities. Participants help determine what is recorded, how it is used and what can be shared.",
    "For Indigenous projects, relevant cultural authority, community-controlled decision processes and consent guide the work. Participation, knowledge access and use arrangements are agreed with the people and organisations concerned, including payment where appropriate.",
    "Clear responsibilities and decision processes are part of project design. We help partners work through how people participate, how shared assets are managed and how disagreements and changing circumstances will be addressed.",
  ],
  flexibleHeading: "Let's work together",
  abundanceHeading: "Abundance through reciprocity",
  abundanceParas: [
    "Abundance grows through relationships of reciprocity. When people share knowledge, care for land and support one another's livelihoods, they build the foundations of lasting prosperity.",
    "Bioregional Ventures brings this principle into project development, enterprise and investment, connecting resources with local priorities and helping value circulate locally. Our aim is to develop productive assets and enterprises that meet real needs, strengthen local ownership and replenish the ecological and social conditions on which they depend.",
  ],
  flexibleBody: "",
  fundingNote:
    "Projects developed through Bioregional Design Studios may require grants, philanthropy, enterprise income or investment. Participation does not imply funding approval or investment.",
  ctaLabel: "Discuss a Design Studio",
  ctaHref: "/contact?topic=design-studio",
};

// --- Approach page: enterprises, funding pathways, success (26 Oct brief) ----

export const approachEnterprises = {
  heading: "Developing projects that strengthen one another",
  body: [
    "A local enterprise often depends on more than its own business plan. Access to land, water, equipment, storage, skills and customers can determine whether it succeeds.",
    "We explore how complementary activities can work together: independent enterprises using shared infrastructure, growers reaching customers through a common market, or productive land supporting food, learning and ecological stewardship.",
    "Each activity needs clear responsibilities and a workable operating model. Shared assets need arrangements for access, maintenance and replacement. These foundations help cooperation deliver practical value over time.",
    "Together, connected projects can grow into a longer-term vision for a place - its landscapes, livelihoods and shared future.",
  ],
  grouping: [
    { title: "Land", text: "Secure access and long-term stewardship arrangements." },
    { title: "Enterprises", text: "Independent businesses with clear operating models." },
    { title: "Shared infrastructure", text: "Equipment, facilities and services used in common." },
    { title: "Stewardship", text: "Care for the ecological and social conditions the work depends on." },
  ],
  groupingNote: "Illustrative project relationships.",
};

export const approachFunding = {
  text: "Different activities need different resources.",
  linkText: "Explore how we help projects assess funding needs and suitable pathways.",
  href: "/funding",
};

export const approachSuccess = {
  heading: "Value that lasts in the place",
  intro: "We assess projects through three connected questions:",
  questions: [
    "Does it improve the condition of the land and ecosystems?",
    "Does it strengthen local capability, ownership and livelihoods?",
    "Can its operating model endure?",
  ],
  outro:
    "The measures depend on the project. They may include changes in ecological condition, access to productive land, paid work, local participation, use of shared assets and the ability to meet ongoing costs.",
};